import { Loader2, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { InterviewQuestionCard } from "./InterviewQuestionCard";
import { ConceptProposalCard } from "./ConceptProposalCard";
import type {
  ConceptCategory,
  ConceptRefineResponse,
  ConceptSuggestion,
} from "@/types/concept";
import { useConceptRefine } from "@/hooks/useConceptRefine";

const MAX_QUESTIONS = 5;

type TopMode = "name" | "prose";
type PanelPhase = "idle" | "loading" | "categories" | "interview" | "proposal" | "error";

export function shouldShowQuestionCaption(args: {
  topMode: TopMode;
  initialName: string;
  phase: PanelPhase;
  interviewHistoryLength: number;
}): boolean {
  const { topMode, initialName, phase, interviewHistoryLength } = args;
  if (topMode !== "name") return false;
  if (initialName.trim() === "") return false;
  if (phase === "categories" || phase === "interview") return true;
  if (phase === "loading" && interviewHistoryLength > 0) return true;
  return false;
}

interface InterviewEntry {
  question: string;
  options: string[];
  answer: string;
}

interface Props {
  initialName: string;
  initialDescription?: string;
  initialKeywords?: string;
  onSelect: (suggestion: ConceptSuggestion) => void;
  onBack: () => void;
  onCreateAnyway: () => void;
}

export function ConceptRefinementPanel({
  initialName,
  initialDescription,
  initialKeywords,
  onSelect,
  onBack,
  onCreateAnyway,
}: Props) {
  const [topMode, setTopMode] = useState<TopMode>(
    initialName.trim() ? "name" : "prose",
  );
  const [phase, setPhase] = useState<PanelPhase>(
    initialName.trim() ? "loading" : "idle",
  );
  const [categories, setCategories] = useState<ConceptCategory[]>([]);
  const [interviewHistory, setInterviewHistory] = useState<InterviewEntry[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<{ question: string; options: string[] } | null>(null);
  const [proposal, setProposal] = useState<ConceptSuggestion | null>(null);
  const [refinementOptions, setRefinementOptions] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [escapeText, setEscapeText] = useState("");
  const [proseText, setProseText] = useState("");
  const [showNotQuite, setShowNotQuite] = useState(false);
  const lastRequestRef = useRef<Record<string, unknown> | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const refine = useConceptRefine();

  function scrollToBottom() {
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    });
  }

  function handleResponse(data: ConceptRefineResponse) {
    // Generated concept (prose extraction / escape hatch) — check before broadness
    if (data.generated) {
      onSelect(data.generated);
      return;
    }
    if (data.broadness === "very_broad" && data.categories?.length) {
      setCategories(data.categories);
      setPhase("categories");
      return;
    }
    if (data.interview_question) {
      setCurrentQuestion(data.interview_question);
      setPhase("interview");
      scrollToBottom();
      return;
    }
    if (data.interview_complete && data.proposal) {
      setProposal(data.proposal);
      setRefinementOptions(data.proposal_refinement_options ?? []);
      setShowNotQuite(false);
      setPhase("proposal");
      scrollToBottom();
      return;
    }
    if (data.broadness === "moderate" || data.broadness === "specific") {
      // Assess returned moderate/specific — start interview (use name as category
      // context). "specific" used to auto-accept, but the user explicitly asked
      // for help, and a bare object name ("ngc 6164/6165") is not a research
      // question (2026-09-27). Include description if user provided one.
      const params: Record<string, unknown> = {
        name: initialName,
        category: selectedCategory || initialName,
      };
      if (initialDescription) params.context = initialDescription;
      fireRefine(params);
      return;
    }
  }

  function fireRefine(params: Record<string, unknown>) {
    lastRequestRef.current = params;
    setPhase("loading");
    refine.mutate(params as never, {
      onSuccess: handleResponse,
      onError: () => setPhase("error"),
    });
  }

  useEffect(() => {
    if (initialName.trim()) {
      fireRefine({ name: initialName, keywords: initialKeywords || undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleCategoryClick(cat: ConceptCategory) {
    setSelectedCategory(cat.label);
    fireRefine({ name: initialName, category: cat.label });
  }

  function handleAnswer(answer: string) {
    if (!currentQuestion) return;
    const newHistory = [...interviewHistory, { ...currentQuestion, answer }];
    setInterviewHistory(newHistory);
    setCurrentQuestion(null);

    const historyForBackend = newHistory.map(({ question, answer: a }) => ({ question, answer: a }));

    if (newHistory.length >= MAX_QUESTIONS) {
      fireRefine({
        name: initialName,
        category: selectedCategory || undefined,
        interview_history: historyForBackend,
        context: "Please propose a concept now based on what you know.",
      });
    } else {
      fireRefine({
        name: initialName,
        category: selectedCategory || undefined,
        interview_history: historyForBackend,
      });
    }
  }

  function handleProposeNow() {
    // Bail out of the interview: propose a concept from the answers so far.
    const historyForBackend = interviewHistory.map(({ question, answer }) => ({ question, answer }));
    setCurrentQuestion(null);
    fireRefine({
      name: initialName,
      category: selectedCategory || undefined,
      interview_history: historyForBackend,
      context: "Please propose a concept now based on what you know.",
    });
  }

  function handleNotQuiteRefine(correction: string) {
    const historyForBackend = interviewHistory.map(({ question, answer }) => ({ question, answer }));
    setShowNotQuite(false);
    fireRefine({
      name: initialName,
      category: selectedCategory || undefined,
      interview_history: historyForBackend,
      context: correction,
    });
  }

  function handleEscapeGo() {
    if (!escapeText.trim()) return;
    // Use prose/extract mode so it generates a concept directly (not freeform → suggestions loop)
    fireRefine({ name: initialName, prose: escapeText.trim() });
  }

  function handleProseSubmit() {
    if (proseText.trim().length < 20) return;
    fireRefine({ name: initialName, prose: proseText.trim() });
  }

  function handleRetry() {
    if (lastRequestRef.current) {
      fireRefine(lastRequestRef.current);
    }
  }

  const isLoading = phase === "loading";

  return (
    <div className="grid gap-4">
      {/* Mode toggle */}
      <div className="flex gap-1 rounded-md bg-muted p-1 text-sm">
        <button
          type="button"
          className={`flex-1 rounded px-2 py-1 transition-colors ${topMode === "name" ? "bg-background font-medium shadow-sm" : "text-muted-foreground"}`}
          onClick={() => setTopMode("name")}
        >
          Start from a name
        </button>
        <button
          type="button"
          className={`flex-1 rounded px-2 py-1 transition-colors ${topMode === "prose" ? "bg-background font-medium shadow-sm" : "text-muted-foreground"}`}
          onClick={() => setTopMode("prose")}
        >
          Start from existing text
        </button>
      </div>

      {topMode === "prose" ? (
        <div className="grid gap-3">
          <p className="text-sm text-muted-foreground">
            Paste an abstract, introduction, or research question to generate a focused concept.
          </p>
          <Textarea
            value={proseText}
            onChange={(e) => setProseText(e.target.value)}
            placeholder="Paste your abstract or research question here..."
            rows={4}
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={handleProseSubmit}
            disabled={isLoading || proseText.trim().length < 20}
            className="w-fit"
          >
            {isLoading ? (
              <><Loader2 className="mr-2 size-4 animate-spin" />Extracting...</>
            ) : (
              <><Sparkles className="mr-2 size-4" />Extract Concept</>
            )}
          </Button>
        </div>
      ) : (
        <div className="grid gap-3">
          {/* Caption: signal interview length up-front */}
          {shouldShowQuestionCaption({
            topMode,
            initialName,
            phase,
            interviewHistoryLength: interviewHistory.length,
          }) && (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Sparkles className="size-3" />
              A few quick questions to focus your concept — propose anytime once it feels right.
            </p>
          )}

          {/* Empty state: name mode but no name to refine */}
          {!initialName.trim() && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-4 text-sm leading-relaxed text-amber-200/90">
              <p>
                Add a name to refine (
                <span className="font-medium text-amber-100">&larr; Back</span>
                ), or switch to{" "}
                <button
                  type="button"
                  onClick={() => setTopMode("prose")}
                  className="font-medium text-amber-100 underline underline-offset-2 hover:text-white"
                >
                  Start from existing text
                </button>{" "}
                to paste an abstract.
              </p>
            </div>
          )}

          {/* Breadcrumb */}
          {selectedCategory && (
            <p className="text-sm text-muted-foreground">
              {initialName} → <span className="font-medium text-primary">{selectedCategory}</span>
            </p>
          )}

          {/* Loading overlay */}
          {isLoading && (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              {interviewHistory.length > 0 ? "Thinking..." : "Analyzing concept..."}
            </div>
          )}

          {/* Error state */}
          {phase === "error" && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm">
              <p className="mb-2 text-destructive">
                {(refine.error as { response?: { status?: number } })?.response?.status === 429
                  ? "Too many requests. Try again later."
                  : "Something went wrong."}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleRetry} className="h-6 text-xs">
                  Retry
                </Button>
                <button type="button" onClick={onCreateAnyway} className="text-xs text-muted-foreground underline">
                  Create manually
                </button>
              </div>
            </div>
          )}

          {/* Categories (very_broad) */}
          {phase === "categories" && (
            <>
              <p className="text-sm font-medium text-amber-400">
                Refining &ldquo;{initialName}&rdquo;
              </p>
              <p className="text-sm text-muted-foreground">
                This covers a huge range of research. What&rsquo;s your angle?
              </p>
              <div className="grid gap-1.5">
                {categories.map((cat) => (
                  <button
                    key={cat.label}
                    type="button"
                    onClick={() => handleCategoryClick(cat)}
                    className="flex items-baseline gap-2 rounded-lg border border-border bg-muted/50 p-3 text-left transition-colors hover:border-primary/50 hover:bg-muted"
                  >
                    <span className="text-sm text-primary">&rsaquo;</span>
                    <div>
                      <span className="text-sm font-semibold">{cat.label}</span>
                      <span className="ml-2 text-xs text-muted-foreground">{cat.examples}</span>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Interview questions + proposal — scrollable when content grows */}
          {(phase === "interview" || (phase === "loading" && interviewHistory.length > 0) || phase === "proposal") && (
            <div
              ref={scrollRef}
              className="grid max-h-[50vh] gap-2 overflow-y-auto pr-1"
            >
              {interviewHistory.map((entry, i) => (
                <InterviewQuestionCard
                  key={i}
                  index={i}
                  question={entry.question}
                  options={entry.options}
                  answered={entry.answer}
                  onAnswer={() => {}}
                  disabled
                />
              ))}
              {currentQuestion && phase === "interview" && (
                <InterviewQuestionCard
                  index={interviewHistory.length}
                  question={currentQuestion.question}
                  options={currentQuestion.options}
                  onAnswer={handleAnswer}
                  disabled={isLoading}
                />
              )}

              {/* Bail out once there's enough context to propose. */}
              {currentQuestion && phase === "interview" && interviewHistory.length >= 1 && (
                <button
                  type="button"
                  onClick={handleProposeNow}
                  disabled={isLoading}
                  className="justify-self-start text-xs text-primary underline underline-offset-2 hover:text-primary/80 disabled:opacity-50"
                >
                  I'm ready — propose a concept now
                </button>
              )}

              {/* Proposal — inside scroll container */}
              {phase === "proposal" && proposal && (
                <ConceptProposalCard
                  proposal={proposal}
                  refinementOptions={refinementOptions}
                  onAccept={onSelect}
                  onRefine={handleNotQuiteRefine}
                  disabled={isLoading}
                />
              )}
            </div>
          )}

          {/* Escape hatch — hidden when "Not quite" is expanded or no name present */}
          {phase !== "error" && !showNotQuite && !isLoading && initialName.trim() && (
            <div className="border-t border-border pt-3">
              <div className="flex gap-2">
                <Input
                  value={escapeText}
                  onChange={(e) => setEscapeText(e.target.value)}
                  placeholder="I already know — describe your concept here..."
                  className="text-xs"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleEscapeGo();
                  }}
                />
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleEscapeGo}
                  disabled={isLoading || !escapeText.trim()}
                  className="shrink-0"
                >
                  Go &rarr;
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between">
        {initialName.trim() ? (
          <button
            type="button"
            onClick={onCreateAnyway}
            className="text-xs text-muted-foreground underline"
          >
            Create as &ldquo;{initialName}&rdquo; anyway
          </button>
        ) : (
          <span />
        )}
        <Button variant="outline" size="sm" onClick={onBack}>
          &larr; Back
        </Button>
      </div>
    </div>
  );
}
